import PropTypes from 'prop-types';
import React from 'react';

import { MONTHS, yearOptions } from 'helpers/careerEntries';
import styles from './EntryDialog.module.scss';

export const errorId = (id) => `${id}-error`;

/** Props for an input that has `error` showing under it. */
export const invalidProps = (id, error) =>
  error ? { 'aria-invalid': true, 'aria-describedby': errorId(id) } : {};

const OptionalTag = () => <span className={styles.optional}>Optional</span>;

export const Field = ({
  id,
  label,
  optional,
  error,
  help,
  aside,
  children,
}) => (
  <div className={styles.field}>
    <label className={styles.label} htmlFor={id}>
      {label}
      {optional && <OptionalTag />}
    </label>
    {children}
    {error && (
      <span id={errorId(id)} className={styles.error}>
        {error}
      </span>
    )}
    {(help || aside) && (
      <div className={styles.helpRow}>
        <span className={styles.help}>{help}</span>
        {aside && <span className={styles.help}>{aside}</span>}
      </div>
    )}
  </div>
);

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  optional: PropTypes.bool,
  error: PropTypes.string,
  help: PropTypes.node,
  aside: PropTypes.node,
  children: PropTypes.node,
};

export const inputClass = (error) =>
  `${styles.input} ${error ? styles.inputInvalid : ''}`;

export const CharCount = ({ value, max }) => (
  <>
    {value.length.toLocaleString('en-GB')} / {max.toLocaleString('en-GB')}
  </>
);

CharCount.propTypes = {
  value: PropTypes.string.isRequired,
  max: PropTypes.number.isRequired,
};

const toNumber = (value) => (value === '' ? '' : Number(value));

export const YearSelect = ({
  id,
  value,
  onChange,
  ahead,
  placeholder,
  error,
  errorFor,
}) => (
  <select
    id={id}
    className={inputClass(error)}
    value={value}
    onChange={(event) => onChange(toNumber(event.target.value))}
    {...invalidProps(errorFor || id, error)}
  >
    <option value="">{placeholder}</option>
    {yearOptions(ahead).map((year) => (
      <option key={year} value={year}>
        {year}
      </option>
    ))}
  </select>
);

YearSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  ahead: PropTypes.number,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  // The id `error` is shown under, when that isn't this select's own.
  errorFor: PropTypes.string,
};

YearSelect.defaultProps = {
  ahead: 0,
  placeholder: 'Year',
};

/** A month and a year select under one legend. */
export const MonthYearField = ({
  id,
  legend,
  value,
  onChange,
  ahead,
  error,
  disabled,
  disabledText,
}) => (
  <fieldset className={styles.fieldset}>
    <legend className={`${styles.label} ${disabled ? styles.labelMuted : ''}`}>
      {legend}
    </legend>
    {disabled ? (
      <div className={styles.placeholderBox}>{disabledText}</div>
    ) : (
      <div className={styles.inline}>
        <label className={styles.srOnly} htmlFor={`${id}-month`}>
          {legend} month
        </label>
        <select
          id={`${id}-month`}
          className={inputClass(error)}
          value={value.month}
          onChange={(event) =>
            onChange({ ...value, month: toNumber(event.target.value) })
          }
          {...invalidProps(id, error)}
        >
          <option value="">Month</option>
          {MONTHS.map((month, index) => (
            <option key={month} value={index}>
              {month}
            </option>
          ))}
        </select>
        <label className={styles.srOnly} htmlFor={`${id}-year`}>
          {legend} year
        </label>
        <YearSelect
          id={`${id}-year`}
          value={value.year}
          onChange={(year) => onChange({ ...value, year })}
          ahead={ahead}
          error={error}
          errorFor={id}
        />
      </div>
    )}
    {error && !disabled && (
      <span id={errorId(id)} className={styles.error}>
        {error}
      </span>
    )}
  </fieldset>
);

MonthYearField.propTypes = {
  id: PropTypes.string.isRequired,
  legend: PropTypes.string.isRequired,
  value: PropTypes.shape({
    month: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  ahead: PropTypes.number,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  disabledText: PropTypes.string,
};

export const Checkbox = ({ id, checked, onChange, children }) => (
  <label className={styles.checkbox} htmlFor={id}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
    {children}
  </label>
);

Checkbox.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};
